import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import { AuthRole } from '../utils/roles';
import { useDispatch } from 'react-redux';
import { apiSlice } from '../store/api/apiSlice';
import { AppDispatch } from '../store';

export interface Profile {
  id: string;
  full_name: string | null;
  role: AuthRole | null;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  /** The effective profile (with viewAs override applied) */
  effectiveProfile: Profile | null;
  /** The actual role (ignoring viewAs) */
  actualRole: AuthRole | null;
  loading: boolean;
  /** True while the profile loads, and until the data cache is cleared for a new user or role. */
  profileLoading: boolean;
  /** Current viewAs role override (null = no override) */
  viewAsRole: AuthRole | null;
  /** Set a role to view the app as (admin only). Pass null to clear. */
  setViewAsRole: (role: AuthRole | null) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const fetchProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id,full_name,role')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('Failed to load profile:', error);
      return null;
    }

    return data as Profile | null;
  } catch (error) {
    console.error('Failed to load profile:', error);
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [viewAsRole, setViewAsRoleState] = useState<AuthRole | null>(null);

  // Track whether init() has completed so onAuthStateChange doesn't double-fetch
  const initDone = useRef(false);
  // Track the userId we already have a profile for to avoid refetching the same one
  const profileUserId = useRef<string | null>(null);

  // The API answers each request for the token's user at their current role,
  // but RTK Query keys its cache by endpoint and arguments only. So when the
  // user or their role changes, the cache is emptied before any page reads or
  // subscribes to it:
  // - until the reset runs, profileLoading stays true, so RequireRole holds
  //   every page on its loading state and none shows the last identity's data;
  // - pages mount only after the reset. A reset that follows a page's first
  //   subscription drops it for good (RTK Query hooks re-subscribe only if
  //   they rendered with one), leaving the page on skeletons.
  const identityScope = `${user?.id || 'signed-out'}:${profile?.role || 'no-role'}`;
  const [apiCacheScope, setApiCacheScope] = useState(identityScope);
  const apiCacheStale = apiCacheScope !== identityScope;

  // A layout effect, so held pages render before the next paint.
  useLayoutEffect(() => {
    if (!apiCacheStale) return;
    dispatch(apiSlice.util.resetApiState());
    setApiCacheScope(identityScope);
  }, [apiCacheStale, dispatch, identityScope]);

  const refreshProfile = async (currentUser?: User | null) => {
    const activeUser = currentUser ?? user;
    if (!activeUser) {
      setProfile(null);
      profileUserId.current = null;
      return;
    }

    setProfileLoading(true);
    try {
      const profileData = await fetchProfile(activeUser.id);
      setProfile(profileData);
      profileUserId.current = activeUser.id;
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    // Fast path: getSession() reads from localStorage, no network call
    const init = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const currentSession = data.session || null;
        setSession(currentSession);
        setUser(currentSession?.user || null);

        if (currentSession?.user) {
          await refreshProfile(currentSession.user);
        } else {
          setProfile(null);
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setSession(null);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
        initDone.current = true;
      }
    };

    init();

    // Listen for auth state changes (sign in/out, token refresh)
    const { data: subscription } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      // Skip if init() hasn't finished yet — it already handles the initial session
      if (!initDone.current) return;

      // Skip TOKEN_REFRESHED events if user hasn't changed (just update the session)
      if (event === 'TOKEN_REFRESHED' && newSession?.user?.id === profileUserId.current) {
        setSession(newSession);
        return;
      }

      try {
        setSession(newSession);
        setUser(newSession?.user || null);

        if (newSession?.user) {
          // Only refetch profile if user actually changed
          if (newSession.user.id !== profileUserId.current) {
            await refreshProfile(newSession.user);
          }
        } else {
          setProfile(null);
          profileUserId.current = null;
        }
      } catch (error) {
        console.error('Auth state change failed:', error);
        setProfile(null);
      }
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  // Only admins can use viewAs
  const setViewAsRole = (role: AuthRole | null) => {
    if (profile?.role !== 'admin') return;
    setViewAsRoleState(role);
  };

  // Build the effective profile with the viewAs override
  const effectiveProfile = useMemo<Profile | null>(() => {
    if (!profile) return null;
    if (!viewAsRole || profile.role !== 'admin') return profile;
    return { ...profile, role: viewAsRole };
  }, [profile, viewAsRole]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user,
      profile: effectiveProfile, // Components see the overridden role
      effectiveProfile,
      actualRole: profile?.role || null,
      loading,
      profileLoading: profileLoading || apiCacheStale,
      viewAsRole,
      setViewAsRole,
      signIn: async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
          throw error;
        }
        const nextSession = data.session || null;
        setSession(nextSession);
        setUser(nextSession?.user || null);
        if (nextSession?.user) {
          await refreshProfile(nextSession.user);
        } else {
          setProfile(null);
        }
        setLoading(false);
      },
      signOut: async () => {
        setViewAsRoleState(null);
        profileUserId.current = null;
        const { error } = await supabase.auth.signOut();
        if (error) {
          throw error;
        }
      },
      refreshProfile: async () => {
        await refreshProfile();
      },
    }),
    [session, user, effectiveProfile, profile, loading, profileLoading, apiCacheStale, viewAsRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
